const API_BASE = '/api/v2/admin'

export class ApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

interface RequestBody {
  [key: string]: unknown
}

async function request(method: string, path: string, body?: RequestBody): Promise<unknown> {
  const url = API_BASE + path
  const opts: RequestInit = {
    method,
    credentials: 'include',
    headers: {},
  }
  if (body) {
    (opts.headers as Record<string, string>)['Content-Type'] = 'application/json'
    opts.body = JSON.stringify(body)
  }
  const response = await fetch(url, opts)
  if (!response.ok) {
    const data = await response.json().catch(() => ({})) as { error?: { message?: string }; message?: string }
    const msg = data.error?.message || data.message || `HTTP ${response.status}`
    throw new ApiError(response.status, msg)
  }
  if (response.status === 204) return undefined
  return response.json()
}

// Types
export interface Car {
  id: number
  vin: string
  is_payed: boolean
  created_at: string
  updated_at: string
  created_by: string
  updated_by: string
}

export interface Device {
  id: number
  hwid: string
  first_seen_at: string
  last_seen_at: string
}

export interface ADBDevice {
  vin: string
  enabled: boolean
  keys: string[]
  remote_port: number
  note: string
  created_at?: string
  updated_at?: string
  last_seen_at?: string
  last_ip?: string
}

export interface LogEntry {
  id: number
  hwid: string | null
  vin: string | null
  endpoint: string
  method: string
  ip: string | null
  status_code: number | null
  severity: string | null
  build_mode: string | null
  partner_tag: string | null
  duration_ms: number | null
  app_version: string | null
  go_version: string | null
  extra: string | null
  created_at: string
}

export interface Activation {
  id: number
  vin: string
  hwid: string | null
  device_id: string | null
  created_at: string
  updated_at: string
  duplicate_vin?: string
}

export interface NewsItem {
  id: number
  title: string
  url: string
  date: string
  enabled: boolean
}

export interface TrustedHWID {
  hwid: string
  created_at: string
  comment: string
}

export interface AuthResponse {
  success: boolean
  message: string
  username?: string
  role?: string
  partner_tag?: string
}

export interface AuthCheckResponse {
  authenticated: boolean
  username?: string
  role?: string
  partner_tag?: string
	impersonating?: boolean
}

export interface Build {
  id: number
  version: string
  partner?: string
  channel: string
  sha256: string
  size: number
  download_sha256?: string
  download_size?: number
  mandatory: boolean
  created_at: string
  download_url?: string
  // Set only on a partner's own rows (partnerBuildEntry in builds.go):
  // download_url there is the session-scoped proxy (for the row's own
  // "Скачать" button, gives the file a partner-branded filename);
  // public_download_url is the real secret-free public link, safe to copy
  // and hand to someone who isn't logged into the panel — same URL an
  // admin's own download_url already is on their rows.
  public_download_url?: string
  // Computed server-side per (channel, partner) — the newest build within
  // that specific scope, not just the first row of a mixed list. See
  // currentBuildIDs in internal/handlers/admin/builds.go.
  is_current?: boolean
}

export interface Partner {
  id: number
  slug: string
  name: string
  token?: string
}

export interface PartnerInstallation {
  id: number
  hwid: string
  partner_tag: string
  partner_name: string
  label: string
  is_active: boolean
  created_at: string
  last_seen_at: string
}

export interface ClusterDevice {
  id: number
  hwid: string
  vin: string
  status: string
  is_blocked: boolean
  expires: string
  created_at: string
  last_seen: string
  note: string
  created_by: string
  updated_by: string
}

export interface GarageCar {
  id: number
  brand: string
  model: string
  year: string
  vin: string
  verified_at: string
  is_payed: boolean
  activated_at: string
  vehicle_device_id: string
  created_at: string
}

export interface ProgressFeature {
  id: number
  label: string
  pct: number
  status: string
  position: number
}

export interface ProgressModelItem {
  id: number
  model: string
  year: string
  brand: string
  status: string
  overall: number
  updated: string
  position: number
  features: ProgressFeature[]
}

export interface SiteNewsItem {
  id: number
  title: string
  body: string
  author: string
  pinned: boolean
  created_at: string
}

export const api = {
  // Auth
  login(username: string, password: string): Promise<AuthResponse> {
    return fetch('/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ username, password }),
    }).then(r => r.json())
  },
  register(username: string, password: string): Promise<AuthResponse> {
    return fetch('/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ username, password }),
    }).then(r => r.json())
  },
  checkAuth(): Promise<AuthCheckResponse> {
    return fetch('/auth/check', { credentials: 'include' }).then(r => r.json())
  },
  logout(): Promise<void> {
    return fetch('/auth/logout', { method: 'POST', credentials: 'include' }).then(() => undefined)
  },

  // Client personal cabinet
  garage: {
    list(): Promise<any> { return request('GET', '/garage') },
    create(data: { brand: string; model: string; year?: string }): Promise<any> { return request('POST', '/garage', data) },
    claim(code: string): Promise<any> { return request('POST', '/garage/pair', { code }) },
    remove(id: number): Promise<any> { return request('DELETE', `/garage/${id}`) },
  },
  progress: {
    list(): Promise<any> { return request('GET', '/progress') },
  },
  siteNews: {
    list(): Promise<any> { return request('GET', '/site-news') },
  },

  // Admin
  admin: {
    getAvailableActions(): Promise<any> {
      return request('GET', '/actions')
    },
  },

  // Cars
  cars: {
    list(limit = 50, offset = 0, search = ''): Promise<any> {
      const q = `/cars?limit=${limit}&offset=${offset}` + (search ? `&search=${encodeURIComponent(search)}` : '')
      return request('GET', q)
    },
    create(data: { tuid?: string; vin: string; is_payed?: boolean }): Promise<any> { return request('POST', '/cars', data) },
    setPaid(vin: string, isPaid: boolean): Promise<any> { return request('POST', `/cars/${vin}/payment`, { is_payed: isPaid }) },
    setUnpaid(vin: string): Promise<any> { return request('POST', `/cars/${vin}/unpaid`) },
    getUIConfig(vin: string): Promise<any> { return request('GET', `/cars/${vin}/ui-config`) },
    setUIConfig(vin: string, visibleActions: string[]): Promise<any> { return request('POST', `/cars/${vin}/ui-config`, { visible_actions: visibleActions }) },
    setCarType(vin: string, carType: string): Promise<any> { return request('POST', `/cars/${vin}/car-type`, { car_type: carType }) },
  },

  // Devices
  devices: {
    list(limit = 50, offset = 0, search = ''): Promise<any> {
      const q = `/devices?limit=${limit}&offset=${offset}` + (search ? `&search=${encodeURIComponent(search)}` : '')
      return request('GET', q)
    },
  },

  // Deepal S05 remote ADB access — server additionally enforces role=admin.
  adb: {
    list(): Promise<any> { return request('GET', '/adb/devices') },
    save(device: ADBDevice): Promise<any> { return request('POST', '/adb/devices', device as unknown as RequestBody) },
    remove(vin: string): Promise<any> { return request('DELETE', `/adb/devices/${encodeURIComponent(vin)}`) },
  },

  // Changan Q07 engineering-mode unlock codes - admin-minted, ahead of VIN
  // activation (see internal/handlers/admin/q07adb.go for why this can't
  // wait for the normal installer-facing JWT-gated flow).
  q07Adb: {
    history(): Promise<any> {
      return request('GET', '/q07/adb_code/history')
    },
    create(data: { vin: string; hwid?: string; login?: string; password?: string; ttl_minutes?: number }): Promise<any> {
      return request('POST', '/q07/adb_code', data as unknown as RequestBody)
    },
    status(vin: string): Promise<any> {
      return request('GET', `/q07/adb_code/status?vin=${encodeURIComponent(vin)}`)
    },
  },

  // Logs
  logs: {
    list(limit = 200, offset = 0, search = ''): Promise<any> {
      const q = `/logs?limit=${limit}&offset=${offset}` + (search ? `&search=${encodeURIComponent(search)}` : '')
      return request('GET', q)
    },
  },

  // Activations
  activations: {
    list(limit = 50, offset = 0, search = ''): Promise<any> {
      const q = `/activations?limit=${limit}&offset=${offset}` + (search ? `&search=${encodeURIComponent(search)}` : '')
      return request('GET', q)
    },
    update(vin: string, data: { device_id?: string; hwid?: string }): Promise<any> { return request('PUT', `/activations/${encodeURIComponent(vin)}`, data) },
  },

  // News
  news: {
    list(): Promise<any> { return request('GET', '/news') },
    create(data: Record<string, unknown>): Promise<any> { return request('POST', '/news', data) },
    update(id: number, data: Record<string, unknown>): Promise<any> { return request('PUT', `/news/${id}`, data) },
    delete(id: number): Promise<any> { return request('DELETE', `/news/${id}`) },
  },

  // HWIDs
  hwids: {
    list(): Promise<any> { return request('GET', '/hwids') },
    add(data: { hwid: string; comment?: string }): Promise<any> { return request('POST', '/hwids', data) },
    remove(hwid: string): Promise<any> { return request('DELETE', `/hwids/${hwid}`) },
  },

  // Partner Trusted HWIDs
  partnerHWIDs: {
    list(): Promise<any> { return request('GET', '/partner-hwids') },
    add(data: { hwid: string; comment?: string }): Promise<any> { return request('POST', '/partner-hwids', data) },
    remove(hwid: string): Promise<any> { return request('DELETE', `/partner-hwids/${hwid}`) },
  },

  // Users
  users: {
    list(): Promise<any> { return request('GET', '/users') },
    create(data: { username: string; password: string; role_id: number; partner_tag?: string }): Promise<any> { return request('POST', '/users', data) },
    update(id: number, data: { password?: string; partner_tag?: string }): Promise<any> { return request('PUT', `/users/${id}`, data) },
    updateRole(id: number, role_id: number): Promise<any> { return request('PUT', `/users/${id}/role`, { role_id }) },
    remove(id: number): Promise<any> { return request('DELETE', `/users/${id}`) },
	impersonate(id: number): Promise<any> { return fetch('/auth/impersonate', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ user_id: id }) }).then(async r => { if (!r.ok) throw new ApiError(r.status, await r.text()); return r.json() }) },
	stopImpersonating(): Promise<any> { return fetch('/auth/impersonate/stop', { method: 'POST', credentials: 'include' }).then(async r => { if (!r.ok) throw new ApiError(r.status, await r.text()); return r.json() }) },
  },

  // Overlays
  overlays: {
    list(group: string = ''): Promise<any> { return request('GET', group ? '/overlays?group=' + encodeURIComponent(group) : '/overlays') },
    listByCarType(carTypeId: number): Promise<any> { return request('GET', '/overlays?car_type_id=' + carTypeId) },
    create(data: FormData): Promise<any> {
      return fetch(API_BASE + '/overlays', { method: 'POST', credentials: 'include', body: data }).then(r => {
        if (!r.ok) return r.json().then(d => { throw new ApiError(r.status, d.error?.message || d.message || `HTTP ${r.status}`) })
        return r.json()
      })
    },
    update(id: number, data: FormData): Promise<any> {
      return fetch(API_BASE + `/overlays/${id}`, { method: 'PUT', credentials: 'include', body: data }).then(r => {
        if (!r.ok) return r.json().then(d => { throw new ApiError(r.status, d.error?.message || d.message || `HTTP ${r.status}`) })
        return r.json()
      })
    },
    delete(id: number): Promise<any> { return request('DELETE', `/overlays/${id}`) },
  },  // CS75 Plus Analytics
  clusterAnalytics: {
    list(limit = 50, offset = 0, search = ''): Promise<any> {
      const q = `/cluster-analytics?limit=${limit}&offset=${offset}` + (search ? `&search=${encodeURIComponent(search)}` : '')
      return request('GET', q)
    },
  },

  // Cluster License Devices
  cluster: {
    list(limit = 50, offset = 0, search = ''): Promise<any> {
      const q = `/cluster/devices?limit=${limit}&offset=${offset}` + (search ? `&search=${encodeURIComponent(search)}` : '')
      return request('GET', q)
    },
    register(data: { hwid: string; vin?: string; status?: string; expires?: string; note?: string }): Promise<any> { return request('POST', '/cluster/devices', data) },
    toggleActive(hwid: string): Promise<any> { return request('POST', `/cluster/devices/${hwid}/toggle_active`) },
    block(hwid: string): Promise<any> { return request('POST', `/cluster/devices/${hwid}/block`) },
    unblock(hwid: string): Promise<any> { return request('POST', `/cluster/devices/${hwid}/unblock`) },
    remove(hwid: string): Promise<any> { return request('DELETE', `/cluster/devices/${hwid}`) },
  },
  // Search
  search(q: string): Promise<any> { return request('GET', '/search?q=' + encodeURIComponent(q)) },

  // DriveBoxActivator builds — partner-role users are auto-scoped server-side
  // to their own partner regardless of the `partner` param they pass here.
  // Per-car-model assets (car_model_assets / MinIO drivebox-car-assets) -
  // the DS05-style bulk pipeline, separate from the /overlays table above.
  carAssets: {
    carTypes(): Promise<any> { return request('GET', '/car_types') },
    list(carType: string): Promise<any> { return request('GET', '/car_model_assets?car_type=' + encodeURIComponent(carType)) },
    create(data: FormData): Promise<any> {
      return fetch(API_BASE + '/car_model_assets', { method: 'POST', credentials: 'include', body: data }).then(r => {
        if (!r.ok) return r.json().then(d => { throw new ApiError(r.status, d.error?.message || d.message || `HTTP ${r.status}`) })
        return r.json()
      })
    },
    // Attaching/repairing the RRO package of an already-published file. The
    // bytes stay put: without this the only fix for an overlay published
    // without its package is a re-upload (or psql).
    setPackage(id: number, packageName: string): Promise<any> {
      return request('PATCH', `/car_model_assets/${id}`, { package_name: packageName })
    },
    delete(id: number): Promise<any> { return request('DELETE', `/car_model_assets/${id}`) },
  },

  builds: {
    list(partner: string = ''): Promise<any> {
      return request('GET', partner ? '/builds?partner=' + encodeURIComponent(partner) : '/builds')
    },
    latest(channel: string = 'stable', partner: string = ''): Promise<any> {
      const q = `/builds/latest?channel=${encodeURIComponent(channel)}` + (partner ? `&partner=${encodeURIComponent(partner)}` : '')
      return request('GET', q)
    },
    delete(id: number): Promise<any> { return request('DELETE', `/builds/${id}`) },
  },

  // Partners (build-download scoping) — admin-only CRUD.
  partners: {
    me(): Promise<any> { return request('GET', '/partners/me') },
    list(): Promise<any> { return request('GET', '/partners') },
    create(data: { slug: string; name: string }): Promise<any> { return request('POST', '/partners', data) },
    update(id: number, data: { name?: string; regenerate_token?: boolean }): Promise<any> { return request('PUT', `/partners/${id}`, data) },
    remove(id: number): Promise<any> { return request('DELETE', `/partners/${id}`) },
    installations(partner: string = ''): Promise<any> {
      return request('GET', partner ? `/partners/installations?partner=${encodeURIComponent(partner)}` : '/partners/installations')
    },
    updateInstallation(id: number, partner: string, data: { label: string; is_active: boolean }): Promise<any> {
      const q = partner ? `?partner=${encodeURIComponent(partner)}` : ''
      return request('PUT', `/partners/installations/${id}${q}`, data)
    },
  },
}
